import '@fontsource/open-sans';

import { alpha, createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      light: '#7AC0F8',
      main: '#000000',
      dark: '#000000',
      contrastText: '#FFFFFF',
    },
    secondary: {
      light: '#ED6696',
      main: '#E10050',
      dark: '#A0003A',
      contrastText: '#FFFFFF',
    },
    info: {
      light: '#808080',
      main: '#000000',
      dark: '#000000',
      contrastText: '#FFFFFF',
    },
    error: {
      light: '#E57373',
      main: '#F44336',
      dark: '#D32F2F',
      contrastText: '#FFFFFF',
    },
    warning: {
      light: '#FFB74D',
      main: '#FF9800',
      dark: '#F57C00',
      contrastText: '#000000',
    },
    success: {
      light: '#81C784',
      main: '#4CAF50',
      dark: '#388E3C',
      contrastText: '#FFFFFF',
    },
    text: {
      primary: '#000000',
      secondary: '#868686',
      disabled: '#000000',
    },
    background: {
      default: '#FFFFFF',
      boxShadow: 'none',
    },
    action: {
      hover: '#F5F5F5',
      selected: '#E0E0E0',
    },
    divider: '#E0E0E0',
  },
  typography: {
    fontFamily: '"Open Sans", sans-serif',
    fontSize: 14,
    h1: {
      fontSize: '2em',
      color: '#868686',
      lineHeight: '60px',
    },
    h2: {
      fontSize: '1.5em',
      color: '#868686',
      lineHeight: '44px',
    },
    h3: {
      fontSize: '1.2em',
      fontWeight: 300,
      color: '#868686',
      lineHeight: '33px',
    },
    h4: {
      fontSize: '1.125em',
      color: '#000000',
      lineHeight: '16px',
    },
    body1: {
      fontSize: 14,
      lineHeight: '19px',
    },
    body2: {
      fontSize: 14,
      lineHeight: '19px',
      fontWeight: 300,
    },
  },
  components: {
    // Button contract (prefer plain <Button> or FormButtons aliases):
    //   contained + primary  → ink CTA
    //   outlined + primary   → ghost / secondary
    //   contained + success  → start / run
    //   contained + error    → destructive filled
    //   outlined + error     → destructive ghost
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
          '&:active': {
            boxShadow: 'none',
          },
        },
        sizeSmall: {
          fontSize: '13px',
          fontWeight: 500,
          letterSpacing: 0,
          padding: '4px 12px',
          minHeight: 28,
          borderRadius: 4,
          '&.MuiButton-contained': {
            letterSpacing: 0,
          },
        },
        sizeMedium: {
          fontSize: '14px',
          fontWeight: 400,
        },
        contained: {
          letterSpacing: '1.25px',
        },
        containedPrimary: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor:
              theme.palette.mode === 'light'
                ? theme.palette.text.secondary
                : theme.palette.primary.dark,
            boxShadow: 'none',
          },
        }),
        containedSuccess: ({ theme }) => ({
          backgroundColor: theme.palette.success.main,
          color: theme.palette.success.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.success.dark,
            boxShadow: 'none',
          },
        }),
        containedError: ({ theme }) => ({
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.error.dark,
            boxShadow: 'none',
          },
        }),
        containedWarning: ({ theme }) => ({
          backgroundColor: theme.palette.warning.main,
          color: theme.palette.warning.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.warning.dark,
            boxShadow: 'none',
          },
        }),
        outlinedPrimary: ({ theme }) => ({
          color: theme.palette.text.secondary,
          borderColor: theme.palette.divider,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.text.disabled,
            color: theme.palette.text.primary,
          },
        }),
        outlinedError: ({ theme }) => ({
          color: theme.palette.error.main,
          borderColor: theme.palette.divider,
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: alpha(theme.palette.error.main, 0.08),
            borderColor: theme.palette.error.main,
          },
        }),
        textPrimary: ({ theme }) => ({
          color: theme.palette.text.primary,
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            '& input.Mui-disabled, & textarea.Mui-disabled': {
              color: '#000000',
              WebkitTextFillColor: '#000000',
              opacity: 0.8,
            },
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterDelay: 2000,
      },
    },
  },
});

export default theme;
